import { AxiosError } from 'axios';
import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';
import { useViewController } from 'lib-ui/vv-uploader/use-view-controller';

import { ContentType } from '../client-portal/autogenerated/http-client';
import { PrepareResponse } from './autogenerated/data-contracts';
import {
    SpectreTokenResponse,
    getSpectreAuthController
} from 'lib-ui/vv-uploader/use-token';
import { UploadStartResponse } from '../client-portal/model/deposit-models';
import toastFunctions from 'lib-ui/toast/Toast';
import useDepositNowStore, {
    getContinueUpload
} from 'core/stores/deposit-now-store';

type UploadRequestIn = {
    depositId: number;
    file: File;
    fileName: string;
    fileSize: number;
};

type RequestIn = [
    request: UploadRequestIn,
    callback: (newF: boolean, p?: number) => void
];

// 1st - api/uploads =  create container for all files = SPECTRE
// 2nd =  startfileupload = starts file upload = VIEW
// 3rd =  spectreAgentUrl = prepare file, get block ids, chunksize etc = SPECTRE
// 4th =  uploadfileblock = upload each block = SPECTRE
// 5th =  api/uploads/dur/ur/commit = finished and assemble uploaded chunks = SPECTRE

function useStartFileUpload(
    options: UseMutationOptions<PrepareResponse, AxiosError, RequestIn> = {}
) {
    const queryClient = useQueryClient();

    const {
        setFileReference,
        spectreAgentUrl,
        setSpectreRequestToken,
        setDepositUniqueReference,
        setSpectreAgentUrl,
        setSpectreControlUrl
    } = useDepositNowStore();
    const viewController = useViewController();

    return useMutation<PrepareResponse, AxiosError, RequestIn>(
        ['start-upload-step-2'],
        async ([file, callback]) => {
            const startUploadResponse = await viewController
                .request<UploadStartResponse>({
                    path: `/api/v1/depositupload/${file.depositId}/startfileupload`,
                    method: 'POST',
                    body: { fileName: file.fileName, size: file.fileSize },
                    secure: true,
                    type: ContentType.Json
                })
                .then((response) => {
                    callback(true);
                    return response.data;
                });
            queryClient.setQueryData(
                ['start-upload-step-1', startUploadResponse.uniqueReference],
                startUploadResponse
            );
            const dur = startUploadResponse.depositUniqueReference;
            const ur = startUploadResponse.uniqueReference;
            setFileReference(file.fileName, { dur, ur });

            const controller = getSpectreAuthController();

            let retry = 0;
            const createSpectreUpload = () => {
                return controller
                    .request({
                        baseURL: spectreAgentUrl,
                        path: `/api/uploads`,
                        method: 'POST',
                        body: {
                            depositId: dur
                        },
                        secure: true,
                        type: ContentType.Json
                    })
                    .then((response) => {
                        return response.data;
                    });
            };

            await createSpectreUpload().catch((error) => {
                console.error('Error creating upload container:', error);

                if (retry < 3) {
                    retry++;
                    createSpectreUpload();
                }

                toastFunctions.error(
                    'There was an error with the server, please try again later'
                );
                throw error; // Rethrow the error to be handled by the mutation
            });

            return controller
                .request<PrepareResponse>({
                    baseURL: spectreAgentUrl,
                    body: { fileName: file.fileName, size: file.fileSize },
                    path: `/api/uploads/${dur}/${ur}`,
                    method: 'PUT',
                    secure: true,
                    type: ContentType.Json
                })
                .then(async (response) => {
                    const bids = response.data.blockIds ?? [];
                    const bSize = response.data.blockSize ?? 0;
                    console.log('Total amount of blocks', bids.length);

                    for (
                        let i = 0;
                        i < bids.length && getContinueUpload();
                        i++
                    ) {
                        const blockId = bids[i];
                        const start = i * bSize;
                        const end = Math.min((i + 1) * bSize, file.fileSize);

                        const formData = new FormData();

                        formData.append('block', file.file.slice(start, end));
                        let attempt = 0;
                        const maxAttempts = 3;
                        await getSpectreAuthController()
                            .request({
                                baseURL: spectreAgentUrl,
                                path: `/api/uploads/${dur}/${ur}/${blockId}`,
                                method: 'PUT',
                                body: formData,
                                secure: true
                            })
                            .then((response) => {
                                const percentCompleted = Math.round(
                                    (i / bids.length) * 100
                                );
                                callback(false, percentCompleted);
                                return response.data;
                            })
                            .catch((error) => {
                                console.error(
                                    'Error uploading file block:',
                                    error
                                );
                                if (attempt < maxAttempts) {
                                    attempt++;
                                    console.log(
                                        `Retrying upload for block ${blockId} (${attempt}/${maxAttempts})`
                                    );
                                    toastFunctions.error(
                                        'There was an error with the server, retrying'
                                    );
                                    return getSpectreAuthController()
                                        .request({
                                            baseURL: spectreAgentUrl,
                                            path: `/api/uploads/${dur}/${ur}/${blockId}`,
                                            method: 'PUT',
                                            body: formData,
                                            secure: true
                                        })
                                        .then((response) => {
                                            const percentCompleted = Math.round(
                                                (i / bids.length) * 100
                                            );
                                            callback(false, percentCompleted);
                                            return response.data;
                                        });
                                } else if (attempt === maxAttempts) {
                                    toastFunctions.error(
                                        'There was an error with the server, please try again later'
                                    );
                                }
                            });
                    }
                    return {
                        blockIds: bids,
                        size: bSize,
                        fileName: file.fileName
                    };
                })
                .then((response) => {
                    if (!getContinueUpload()) return;
                    return getSpectreAuthController()
                        .request({
                            baseURL: spectreAgentUrl,
                            path: `/api/uploads/${dur}/${ur}/commit`,
                            method: 'POST',
                            secure: true,
                            body: JSON.stringify(response),
                            type: ContentType.Json
                        })
                        .then((response) => {
                            queryClient.refetchQueries([
                                'use-agreements-deposits-files',
                                file.depositId
                            ]);

                            return response.data;
                        })
                        .catch(async (error) => {
                            console.error(
                                'INTERNAL Token expired. Refetching',
                                error
                            );
                            const response =
                                await viewController.request<SpectreTokenResponse>(
                                    {
                                        path: `/api/v1/deposits/${file.depositId}/spectretoken`,
                                        method: 'GET',
                                        secure: true,
                                        format: 'json'
                                    }
                                );
                            setSpectreAgentUrl(
                                response?.data?.spectreControlUrl
                            );
                            setSpectreControlUrl(
                                response?.data?.spectreAgentUrl
                            );
                            setDepositUniqueReference(response?.data?.id);
                            setSpectreRequestToken(
                                response.data.authToken.token
                            );
                        });
                })
                .catch(async (error) => {
                    console.error('EXTERNAL Token expired. Refetching', error);
                    const response =
                        await viewController.request<SpectreTokenResponse>({
                            path: `/api/v1/deposits/${file.depositId}/spectretoken`,
                            method: 'GET',
                            secure: true,
                            format: 'json'
                        });
                    setSpectreAgentUrl(response?.data?.spectreControlUrl);
                    setSpectreControlUrl(response?.data?.spectreAgentUrl);
                    setDepositUniqueReference(response?.data?.id);
                    setSpectreRequestToken(response.data.authToken.token);
                });
        },
        options
    );
}

export default useStartFileUpload;
